/**
 * UAS Audit Logs page - filterable audit log table
 */

import { useState } from 'react'
import {
  ScrollText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUASAuditLogs } from '@/hooks/use-uas-audit-logs'
import type { UASAuditAction } from '@/types/uas'

const AUDIT_ACTIONS: readonly UASAuditAction[] = [
  'ATTRIBUTE_SET',
  'ATTRIBUTE_DELETED',
  'ATTRIBUTE_QUERIED',
  'SUBJECT_CREATED',
  'SUBJECT_UPDATED',
  'SUBJECT_DEACTIVATED',
  'IDP_CONNECTED',
  'IDP_TESTED',
  'IDP_SYNC_STARTED',
  'IDP_SYNC_COMPLETED',
  'BULK_IMPORT',
]

export function IdentityAuditLogsPage() {
  const [page, setPage] = useState(1)
  const [actionFilter, setActionFilter] = useState<string>('all')

  const { data, isLoading } = useUASAuditLogs({
    page,
    pageSize: 20,
    action: actionFilter !== 'all' ? (actionFilter as UASAuditAction) : undefined,
  })

  const logs = data?.data ?? []
  const pagination = data?.pagination

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <ScrollText className="h-4 w-4" />
          <h1 className="text-base font-medium">Identity Audit Logs</h1>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select
            value={actionFilter}
            onValueChange={(val) => {
              setActionFilter(val)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {AUDIT_ACTIONS.map((action) => (
                <SelectItem key={action} value={action}>
                  {action.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Attribute</TableHead>
                <TableHead>Old Value</TableHead>
                <TableHead>New Value</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    No audit logs found.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {log.action.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {log.actorType}
                      {log.actorId && ` (${log.actorId.slice(0, 8)}...)`}
                    </TableCell>
                    <TableCell className="text-xs">
                      {log.entityType ?? '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {log.attributeKey ?? '-'}
                    </TableCell>
                    <TableCell className="max-w-[100px] truncate text-xs text-muted-foreground">
                      {log.oldValue != null ? JSON.stringify(log.oldValue) : '-'}
                    </TableCell>
                    <TableCell className="max-w-[100px] truncate text-xs">
                      {log.newValue != null ? JSON.stringify(log.newValue) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages} ({pagination.totalItems} total)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasPreviousPage}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!pagination.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
